import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import axios from "axios";
import { useForm } from "react-hook-form";
import { useOutletContext, useNavigate } from "react-router-dom";
import { Client_View } from "../../../utils/sweetalert";
import toastr from "../../../utils/toast";

function AddProspect({ onClose, companyinfo_id, prefillName, prefillWebsite }) {
  const { setGlobalLoading, token } = useOutletContext();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm();

  const [data, setData] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [countries, setCountries] = useState([]);
  const [majorIndustries, setMajorIndustries] = useState([]);
  const [minorIndustries, setMinorIndustries] = useState([]);
  const [users, setUsers] = useState([]);
  const [companyinfos, setcompanyinfos] = useState([]);
  const [peVcReferred, setpeVcReferred] = useState([]);
  const [showOthergroupedCompany, setshowOthergroupedCompany] = useState(false);
  const [activeUsers, setActiveUsers] = useState([]);
  const [pipelineStatus, setPipelineStatus] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [pevcOptions, setPevcOptions] = useState([]);
  const [selectedPevc, setSelectedPevc] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);

  // ── NEW: track whether prefill has run so it only fires once ──────────────
  const prefillDoneRef = useRef(false);

  const searchTimeout = useRef(null);
  const lastSelectedRef = useRef(null);

  const peVcReferredWatch = watch("groupedCompanyOption");

  // ── Existing: fetch all dropdown master data ───────────────────────────────
  async function fetchAllDropdownData() {
    setGlobalLoading(false);
    try {
      const res = await axios.get(`${API_BASE_URL}/clients/clientd`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = res.data;
      setData(d);
      setCompanies(d.companies || []);
      setCountries(d.countries || []);
      setMajorIndustries(d.majorIndustry || []);
      setUsers(d.users || []);
    } catch (error) {
      console.error("Error fetching form data:", error);
    }
  }

  async function getActiveUser() {
    const res = await axios.get(`${API_BASE_URL}/employeeinfos/getActiveUser`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setActiveUsers(res.data.options);
  }

  async function getPipeLineStatus() {
    const res = await axios.get(`${API_BASE_URL}/companyinfo/pipelinestatus`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setPipelineStatus(res.data.pipeline_status);
  }

  useEffect(() => {
    fetchAllDropdownData();
    getActiveUser();
    getPipeLineStatus();
  }, [token, API_BASE_URL]);

  // ── Existing: if companyinfo_id passed (other modules) → ViewCompanyInfo ──
  useEffect(() => {
    if (companyinfo_id) {
      ViewCompanyInfo(companyinfo_id);
    }
  }, [companyinfo_id]);

  // ── NEW: prefill from Lusha company row ───────────────────────────────────
  // Runs once after master data is ready (majorIndustries signals that
  // fetchAllDropdownData has completed). Only fires when prefillName is given.
  useEffect(() => {
    if (!prefillName) return; // not called from Prospecting — skip
    if (prefillDoneRef.current) return; // already ran — skip
    if (!majorIndustries.length) return; // master data not ready yet — wait

    prefillDoneRef.current = true;
    runPrefill();
  }, [prefillName, majorIndustries]); // re-checks when master data arrives

  const runPrefill = async () => {
    try {
      const res = await axios.post(
        `${API_BASE_URL}/clients/search_data`,
        { keywords: prefillName },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const results = res.data.companies || [];

      // Build options the same way get_companies_data does
      const options = results.map((c) => ({
        id: c.id,
        name: c.name,
        tbl: c.tbl,
      }));

      if (options.length === 0) {
        // ── No DB match → treat as custom entry, pre-fill name + website ──
        const customOption = {
          id: prefillName,
          name: prefillName,
          tbl: "custom",
        };
        setCompanyOptions([customOption]);
        setSelectedCompany(customOption);
        lastSelectedRef.current = customOption;
        setValue("companies", prefillName);
        if (prefillWebsite) applyWebsitePrefill(prefillWebsite);
        return;
      }

      // Always load options into the dropdown
      setCompanyOptions(options);

      // ── Exactly one companyinfos match → auto-select ──────────────────────
      const companyinfoMatches = options.filter(
        (o) => o.tbl === "companyinfos",
      );
      const clientMatches = options.filter((o) => o.tbl === "companies");

      if (options.length === 1) {
        const only = options[0];
        if (only.tbl === "companies") {
          // Single existing client match → trigger existing client flow
          await AlreadyExitClient(only);
          return;
        }
        if (only.tbl === "companyinfos") {
          // Single companyinfo match → auto-select and populate
          await handleSelectCompany(only);
          return;
        }
      }

      // ── Multiple matches → pre-load dropdown, user picks (Option A) ───────
      // Just leave options loaded; Select will show the menu on focus/click.
      // If there is exactly one companyinfos and zero clients, auto-select it.
      if (companyinfoMatches.length === 1 && clientMatches.length === 0) {
        await handleSelectCompany(companyinfoMatches[0]);
        return;
      }

      // Otherwise: multiple real choices — user picks from pre-loaded dropdown
      // Set the input value so they can see what was searched
    } catch (err) {
      console.error("Prefill search error:", err);
    }
  };

  // ── NEW helper: apply website from Lusha only if DB didn't provide one ────
  const applyWebsitePrefill = (fqdn) => {
    if (!fqdn) return;
    const current = watch("website");
    if (!current || current.trim() === "") {
      const url = fqdn.startsWith("http") ? fqdn : `https://${fqdn}`;
      setValue("website", url);
    }
  };

  // ── NEW helper: shared select logic (used by both prefill and manual pick) ─
  const handleSelectCompany = async (option) => {
    if (option.tbl === "companies") {
      await AlreadyExitClient(option);
      return;
    }
    if (option.tbl === "companyinfos") {
      await AlreadyExitCompanyInfo(option.id);
    }
    const check = await checkSelectedCompany(option.id);
    if (check === false) return;

    setValue("companies", option.id);
    setSelectedCompany(option);
    lastSelectedRef.current = option;

    // After ViewCompanyInfo populates website, applyWebsitePrefill runs
    // via the companyinfos useEffect below (same as existing flow).
    // We store prefillWebsite in a ref so the useEffect can access it.
    pendingWebsitePrefillRef.current = prefillWebsite || null;
  };

  // ── NEW: ref so the companyinfos useEffect can reach prefillWebsite ───────
  const pendingWebsitePrefillRef = useRef(null);

  // ── Existing: populate fields when companyinfos object changes ────────────
  useEffect(() => {
    if (Object.keys(companyinfos).length === 0) return;

    const website = companyinfos?.website || "";
    if (website.trim() === "") {
      // DB has no website — use Lusha fqdn if available
      if (pendingWebsitePrefillRef.current) {
        const fqdn = pendingWebsitePrefillRef.current;
        const url = fqdn.startsWith("http") ? fqdn : `https://${fqdn}`;
        setValue("website", url);
      } else {
        setValue("website", "");
      }
    } else if (
      !website.startsWith("http://") &&
      !website.startsWith("https://")
    ) {
      setValue("website", `http://${website}`);
    } else {
      setValue("website", companyinfos.website);
    }

    const initIndustries = async () => {
      const major = majorIndustries.find(
        (m) => m.id === companyinfos.industrymaster_id,
      );
      if (major) {
        setValue("industrymaster_id", major.id || "");
        await fetchMinorIndustry({ target: { value: major.id } });
      }
    };
    initIndustries();
  }, [companyinfos, majorIndustries]);

  useEffect(() => {
    const minor = minorIndustries.find(
      (m) => m.id === companyinfos.industry_id,
    );
    if (minor) setValue("industry_id", minor.id || "");
  }, [minorIndustries]);

  // ── Existing: all unchanged functions below ───────────────────────────────

  async function get_companies_data(e) {
    const res = await axios.post(
      `${API_BASE_URL}/clients/search_data`,
      { keywords: e },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    let options = [];
    const comps = res.data.companies;
    comps.forEach((company) => {
      options.push({ id: company.id, name: company.name, tbl: company.tbl });
    });
    const exactMatch = options.some(
      (item) => item.name.toLowerCase() === e.toLowerCase(),
    );
    if (!exactMatch && e.trim() !== "") {
      options.unshift({ id: e, name: e, tbl: "custom" });
    }
    setCompanyOptions(options);
  }

  async function getPevcData(keyword) {
    const res = await axios.post(
      `${API_BASE_URL}/clients/companyInfoLoad`,
      { keywords: keyword },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    const options = (res.data.options || []).map((item) => ({
      value: item.value,
      label: item.label,
    }));
    const exactMatch = options.some(
      (item) => item.label.toLowerCase() === keyword.toLowerCase(),
    );
    if (!exactMatch && keyword.trim() !== "") {
      options.unshift({ value: keyword, label: keyword, custom: true });
    }
    setPevcOptions(options);
  }

  const clearSelect = () => {
    setSelectedCompany(null);
    setValue("website", "");
    setValue("companies", "");
    setcompanyinfos([]);
    setValue("industrymaster_id", "");
    setValue("industry_id", "");
    setValue("companyinfo_id", "");
    setValue("origin", "");
    setValue("poc", "");
    setValue("bd_region_id", "");
    setValue("status", "");
    pendingWebsitePrefillRef.current = null;
  };

  const AlreadyExitClient = async (option) => {
    Client_View({
      title: "",
      text: `${option.name} is already an existing client`,
      icon: "warning",
      confirmButtonText: "View Client",
      denyButtonText: "<i class='fa fa-plus'></i> Add Prospect",
      confirmCallback: () => ViewClient(option.id),
      denyCallback: () => {
        onClose?.(option.id);
      },
      cancleCallback: () => clearSelectedCompany(),
    });
  };

  const ViewClient = (id) => {
    onClose();
    setGlobalLoading(true);
    setTimeout(() => {
      navigate(`/client/view/${id}`);
    }, 1000);
  };

  const AlreadyExitCompanyInfo = async (id) => {
    ViewCompanyInfo(id);
  };

  const clearSelectedCompany = () => {
    clearSelect();
  };

  const ViewCompanyInfo = async (id) => {
    try {
      setGlobalLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/clients/getcompanyinfo/${id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const ci = response.data.companyinfo;
      setValue("companies", id);
      setcompanyinfos(ci);
      if (companyinfo_id) {
        setSelectedCompany({ id: ci.id, name: ci.name });
      }
    } catch (error) {
      console.log(error);
      setGlobalLoading(false);
    } finally {
      setGlobalLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      setGlobalLoading(true);
      const response = await axios.post(
        `${API_BASE_URL}/companyinfo/AddCompanyinfo`,
        { data },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onClose();
      toastr.success(response.data.message);
      if (companyinfo_id) {
        setTimeout(() => {
          navigate(`/lead_dashboard/pipeline`);
        }, 1000);
      }
    } catch (err) {
      setGlobalLoading(false);
      console.error("Submission failed:", err);
      toastr.error(err.response?.data?.message || "Failed to submit form");
    } finally {
      setGlobalLoading(false);
    }
  };

  const fetchMinorIndustry = async (e) => {
    const id = e.target.value;
    try {
      const response = await axios.get(
        `${API_BASE_URL}/common_controller/get_minor_industries_by_maji_id/${id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setMinorIndustries(response.data.minor_industries || []);
    } catch (error) {
      console.log(error);
      setGlobalLoading(false);
    } finally {
      setGlobalLoading(false);
    }
  };

  const setpeVcReferredOption = (e) => {
    setValue("private_equity", e);
    setpeVcReferred(e);
  };

  async function checkSelectedCompany(id) {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/clients/getcompanyinfo/${id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (Number(res.data?.companyinfo?.pipeline) === 1) {
        toastr.error("This company is already added to pipeline");
        clearSelect();
        setSelectedCompany(null);
        return false;
      }
    } catch (error) {
      console.log(error);
      toastr.error(error);
    }
  }

  useEffect(() => {
    register("companyinfo_id", {
      required: peVcReferred === "yes" ? "required" : false,
    });
  }, [register, peVcReferred]);

  return (
    <div className="modal fade show d-block add_client" tabIndex="-1">
      <div
        className="modal-dialog modal-dialog-centered modal-md add_client_modal"
        role="document"
      >
        <div className="modal-content">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="modal-header">
              <h4 className="modal-title">Add Prospective Client</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                onClick={() => {
                  clearSelect();
                  onClose?.();
                }}
              >
                <i className="ti ti-x"></i>
              </button>
            </div>

            <div className="modal-body row" style={{ padding: "30px" }}>
              <div className="col-xl-6">
                {/* Company */}
                <div className="row mb-3 me-3">
                  <label className="form-label">
                    Company{" "}
                    <span className="text-danger">
                      * {errors.companies && errors.companies.message}
                    </span>
                  </label>
                  <Select
                    className="react-select"
                    options={companyOptions}
                    value={selectedCompany}
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => option.id}
                    // ── NEW: show a subtle hint when prefillName drove the search ──
                    placeholder={
                      prefillName
                        ? `Tap to select option for "${prefillName}"...`
                        : "Search company..."
                    }
                    formatOptionLabel={(option) => (
                      <div className="d-flex align-items-center">
                        <span>{option.name}</span>
                        {option.tbl === "companies" && (
                          <span className="badge bg-outline-primary ms-2">
                            Client
                          </span>
                        )}
                      </div>
                    )}
                    onChange={async (option) => {
                      // ── same logic as before, now also applies website prefill ──
                      if (option.tbl === "companies") {
                        await AlreadyExitClient(option);
                        return;
                      } else if (option.tbl === "companyinfos") {
                        await AlreadyExitCompanyInfo(option.id);
                      }
                      const check = await checkSelectedCompany(option.id);
                      if (check === false) return;

                      setValue("companies", option.id);
                      setSelectedCompany(option);
                      lastSelectedRef.current = option;

                      // ── NEW: store prefillWebsite for the companyinfos useEffect ──
                      pendingWebsitePrefillRef.current = prefillWebsite || null;
                    }}
                    onInputChange={(value, actionMeta) => {
                      if (actionMeta.action === "input-change") {
                        if (value.trim() !== "") {
                          setSelectedCompany(null);
                        } else {
                          setSelectedCompany(lastSelectedRef.current);
                        }
                        if (searchTimeout.current)
                          clearTimeout(searchTimeout.current);
                        searchTimeout.current = setTimeout(() => {
                          if (value.trim() !== "")
                            get_companies_data(value.trim());
                        }, 500);
                      }
                    }}
                  />
                </div>

                {/* Major Industry */}
                <div className="row mb-3 me-3">
                  <label className="form-label">
                    Major Industry{" "}
                    <span className="text-danger">
                      *{" "}
                      {errors.industrymaster_id &&
                        errors.industrymaster_id.message}
                    </span>
                  </label>
                  <select
                    {...register("industrymaster_id", { required: "required" })}
                    onChange={(e) => fetchMinorIndustry(e)}
                    className="form-select"
                  >
                    <option value="">Select</option>
                    {majorIndustries.map((m) => (
                      <option key={m.id} value={Number(m.id)}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* GCC */}
                <div className="row mb-3 me-3">
                  <label className="form-label">
                    Global Capability Center (GCC){" "}
                    <span className="fs-8">[ e.g. Is this a part of GCC]</span>
                  </label>
                  <select
                    {...register("gcc", { value: "no" })}
                    className="form-select"
                  >
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>

                {/* BD Influence */}
                <div className="row mb-3 me-3">
                  <label className="form-label">BD Influence</label>
                  <select {...register("bdInfluence")} className="form-select">
                    <option value="">Select</option>
                    {users.length > 0 &&
                      users.map((item, index) => (
                        <option key={index} value={Number(item.id)}>
                          {`${item.firstname} ${item.lastname}`}
                        </option>
                      ))}
                  </select>
                </div>

                {/* BD Region */}
                <div className="row mb-3 me-3">
                  <label className="form-label">
                    BD Region{" "}
                    <span className="text-danger">
                      * {errors.bd_region_id && errors.bd_region_id.message}
                    </span>
                  </label>
                  <select
                    {...register("bd_region_id", { required: "required" })}
                    className="form-select"
                  >
                    <option value="">Select</option>
                    <option value={1}>India</option>
                    <option value={2}>International</option>
                  </select>
                </div>

                {/* Status */}
                <div className="row mb-3 me-3">
                  <label className="form-label">
                    Status{" "}
                    <span className="text-danger">
                      * {errors.status && errors.status.message}
                    </span>
                  </label>
                  <select
                    {...register("status", { required: "required" })}
                    className="form-select"
                    defaultValue={1}
                  >
                    <option value="">Select</option>
                    {pipelineStatus.length > 0 &&
                      pipelineStatus.slice(0, -1).map((s) => (
                        <option key={s.id} value={Number(s.id)}>
                          {s.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <div className="col-xl-6">
                {/* Website */}
                <div className="row mb-3 me-3">
                  <label className="form-label">
                    Website{" "}
                    <span className="text-danger">
                      * {errors.website && errors.website.message}
                    </span>
                  </label>
                  <input
                    type="url"
                    {...register("website", { required: "required" })}
                    placeholder="https://www.example.com"
                    className="form-control"
                  />
                </div>

                {/* Minor Industry */}
                <div className="row mb-3 me-3">
                  <label className="form-label">
                    Minor Industry{" "}
                    <span className="text-danger">
                      * {errors.industry_id && errors.industry_id.message}
                    </span>
                  </label>
                  <select
                    {...register("industry_id", { required: "required" })}
                    className="form-select"
                    onChange={(e) => setValue("industry_id", e.target.value)}
                  >
                    <option value="">Select</option>
                    {minorIndustries.length > 0 &&
                      minorIndustries.map((m) => (
                        <option key={m.id} value={Number(m.id)}>
                          {m.name}
                        </option>
                      ))}
                  </select>
                </div>

                {/* PE/VC Referred */}
                <div className="row mb-3 me-3">
                  <label className="form-label">
                    PE/VC Referred{" "}
                    <span className="text-danger">
                      * {errors.private_equity && errors.private_equity.message}
                    </span>
                  </label>
                  <select
                    {...register("private_equity", {
                      required: "required",
                      value: "no",
                    })}
                    onChange={(e) => setpeVcReferredOption(e.target.value)}
                    className="form-select"
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                  {peVcReferred === "yes" && (
                    <span className="mt-2" style={{ padding: "0" }}>
                      {errors.peVcReferred && (
                        <span className="text-danger">
                          {errors.peVcReferred.message}
                        </span>
                      )}
                      <Select
                        className="mt-2"
                        options={pevcOptions}
                        value={selectedPevc}
                        onChange={(option) => {
                          setSelectedPevc(option);
                          if (option?.custom) {
                            setValue("companyinfo_id", option.label, {
                              shouldValidate: true,
                            });
                          } else {
                            setValue("companyinfo_id", option?.value || "", {
                              shouldValidate: true,
                            });
                          }
                        }}
                        onInputChange={(value, actionMeta) => {
                          if (actionMeta.action === "input-change") {
                            if (searchTimeout.current)
                              clearTimeout(searchTimeout.current);
                            searchTimeout.current = setTimeout(() => {
                              if (value.trim() !== "")
                                getPevcData(value.trim());
                            }, 500);
                          }
                        }}
                        placeholder="Search PE/VC"
                        isClearable
                      />
                    </span>
                  )}
                </div>

                {/* Head Quarters */}
                <div className="row mb-3 me-3">
                  <label className="form-label">
                    Head Quarters{" "}
                    <span className="text-danger">
                      * {errors.origin && errors.origin.message}
                    </span>
                  </label>
                  <select
                    {...register("origin", { required: "required" })}
                    className="form-select"
                  >
                    <option value="">Select</option>
                    {countries.map((c) => (
                      <option key={c.name} value={Number(c.id)}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* POC */}
                <div className="row mb-3 me-3">
                  <label className="form-label">
                    Assigned To (POC){" "}
                    <span className="text-danger">
                      * {errors.poc && errors.poc.message}
                    </span>
                  </label>
                  <select
                    {...register("poc", { required: "required" })}
                    className="form-select"
                  >
                    <option value="">Select</option>
                    {activeUsers.length > 0 &&
                      activeUsers.map((u) => (
                        <option key={u.id} value={Number(u.id)}>
                          {u.Name} - ({u.name})
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="modal-footer justify-content-center gap-2">
              <button type="submit" className="btn btn-primary me-2">
                Add Prospect
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  clearSelect();
                  onClose?.();
                }}
              >
                Close
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AddProspect;
